import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import CpntForm from "../../../components/forms/CpntForm";
import CpntFormSection from "../../../components/forms/CpntFormSection";
import CpntFormSectionContainer from "../../../components/forms/CpntFormSectionContainer";
import TextInput from "../../../components/forms/TextInput";
import PhoneInputField from "../../../components/forms/PhoneInputField";
import TextAreaInput from "../../../components/forms/TextAreaInput";
import SelectInput from "../../../components/forms/SelectInput";
import { IContactForm } from "../../../models/interfaces/IContactForm";
import { useFetchSujetContactsQuery } from "../../../redux/slice/api/staticData";
import { nousContacterSchema } from "./nousContacterSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { MESSAGE_PLACEHOLDER_EMAIL, MESSAGE_PLACEHOLDER_NOM, MESSAGE_PLACEHOLDER_TELEPHONE } from "../../../data/message-placeholders";
import { useMailNousContacterMutation } from "../../../redux/slice/api/gestionApi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Type_Return_Message } from "../../../models/interfaces/IReturn_Parent";

interface IProps {
  show: boolean;
  idDefaultSujet?: number | undefined;
  onHide: () => void;
}

declare global {
  interface Window {
    callbackCaptcha: (token: string) => void;
    grecaptcha: any;
  }
}

const ModalContact: React.FC<IProps> = (props) => {
  const { data: sujets } = useFetchSujetContactsQuery();
  const [token, setToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const captchaRef = useRef<HTMLDivElement>(null);
  const [sendMail] = useMailNousContacterMutation();
  const navigate = useNavigate();

  const formMethods = useForm<IContactForm>({
    mode: "all",
    reValidateMode: "onChange",
    shouldFocusError: true,
    resolver: yupResolver(nousContacterSchema),
  });
  const { watch, handleSubmit,setValue } = formMethods;
  const watchAll = watch();

  const callbackCaptcha = (token: React.SetStateAction<string | null>) => {
    setToken(token);
  };

  useEffect(() => {
    if (sujets !== undefined && props.idDefaultSujet !== undefined) {
      setValue("IdSujet", props.idDefaultSujet);
    }
  }, [sujets]);

  useEffect(() => {
    if (props.show) {
      setCaptchaKey((prevKey) => prevKey + 1);
      
     
      // Ajouter le script reCAPTCHA uniquement si nécessaire
      if (!document.querySelector('script[src="https://www.google.com/recaptcha/api.js"]')) {
        const script = document.createElement("script");
        script.src = "https://www.google.com/recaptcha/api.js";
        script.async = true;
        script.defer = true;
        script.onload = () => {
          // Lorsque le script est chargé, nous pouvons initialiser le callback
          window.callbackCaptcha = callbackCaptcha;
        };
        document.body.appendChild(script);
      } else {
        // Si le script est déjà chargé, on initialise immédiatement le callback
        window.callbackCaptcha = callbackCaptcha;
        // Assurez-vous de rendre/reinitialiser le reCAPTCHA si nécessaire
      if (captchaRef.current) {
        window.grecaptcha.render(captchaRef.current, {
          sitekey: process.env.REACT_APP_RECAPTCHA_SITE_KEY,
          callback: window.callbackCaptcha,
        });
      }
      }

    }

  }, [props.show]); // Effet déclenché par l'ouverture de la modale

 

  useEffect(() => {
  }, [captchaKey]);

  const onSubmit = async (data: any) => {
    try {
      const response = await sendMail({ ...data, Token: token }).unwrap();
      if (response.Return_IdType_Message === Type_Return_Message.Erreur) {
        toast.error(response.Return_Message);
      } else {
        toast.success("Votre demande a bien été prise en compte");
        navigate("/");
      }
    } catch (error) {
      toast.error("Erreur lors de la soumission de votre demande");
    }
  };

  const MESSAGE_SIZE = 600;
  const isDisabled = () =>
    !watchAll.Email ||
    !watchAll.Nom ||
    !watchAll.Message ||
    watchAll.IdSujet === 0 ||
    !token;

  return (
    <Modal {...props}>
      <Modal.Header closeButton>
        <div className="fw-bold">
          <h5>Nous Contacter</h5>
        </div>
      </Modal.Header>
      <Modal.Body style={{ textAlign: "left" }}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <CpntForm>
              <CpntFormSection>
                <CpntFormSectionContainer ColumnsSize={12}>
                  <TextInput
                    formName="Nom"
                    label="Prénom & Nom"
                    ismandatory={true}
                    placeholder={MESSAGE_PLACEHOLDER_NOM}
                  />
                </CpntFormSectionContainer>
                <CpntFormSectionContainer ColumnsSize={12}>
                  <TextInput
                    formName="Email"
                    label="Adresse Email"
                    ismandatory={true}
                    placeholder={MESSAGE_PLACEHOLDER_EMAIL}
                  />
                </CpntFormSectionContainer>
                <CpntFormSectionContainer ColumnsSize={12}>
                  <PhoneInputField
                    formName="Telephone"
                    label="Téléphone"
                    ismandatory={true}
                    placeholder={MESSAGE_PLACEHOLDER_TELEPHONE}
                  />
                </CpntFormSectionContainer>
                <CpntFormSectionContainer ColumnsSize={12}>
                  <SelectInput
                    formName="IdSujet"
                    label="Sujet"
                    options={sujets?.Sujets}
                    ismandatory={true}
                    nullValue="-"
                  />
                </CpntFormSectionContainer>
                <CpntFormSectionContainer ColumnsSize={12}>
                  <TextAreaInput
                    formName="Message"
                    rows={10}
                    maxLength={MESSAGE_SIZE}
                    ismandatory={true}
                  />
                </CpntFormSectionContainer>
                <CpntFormSectionContainer ColumnsSize={12}>
                  <div
                    className="g-recaptcha"
                    ref={captchaRef}
                    data-sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    data-callback="callbackCaptcha"
                  />
                  <div className="d-flex flex-column align-items-end h-100">
                    <button
                      className="btn btn-primary mt-auto"
                      type="submit"
                      disabled={isDisabled()}
                    >
                      Envoyer
                    </button>
                  </div>
                </CpntFormSectionContainer>
              </CpntFormSection>
            </CpntForm>
          </form>
        </FormProvider>
      </Modal.Body>
    </Modal>
  );
};

export default ModalContact;
